/**
 * WatchButton Component
 *
 * Allows users to watch/unwatch issues to receive notifications
 * about updates. Shows current watch status and watcher count.
 */

import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { EyeIcon as EyeIconSolid } from '@heroicons/react/24/solid';
import enhancedApiService from '../../api/enhancedApiService';
import { useUI } from '../../contexts/UIContext';

const WatchButton = ({ issueId, onWatchersChange }) => {
  const [isWatching, setIsWatching] = useState(false);
  const [watcherCount, setWatcherCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const { showNotification } = useUI();

  useEffect(() => {
    if (issueId && issueId !== 'new') {
      loadWatchStatus();
    }
  }, [issueId]);

  const loadWatchStatus = async () => {
    try {
      const response = await enhancedApiService({
        method: 'GET',
        url: `/issues/${issueId}/watchers`
      });

      if (response.success) {
        setIsWatching(response.is_watching || false);
        setWatcherCount(response.count || 0);

        if (onWatchersChange) {
          onWatchersChange(response.watchers || []);
        }
      }
    } catch (error) {
      console.error('Error loading watch status:', error);
    } finally {
      setInitialLoad(false);
    }
  };

  const handleToggleWatch = async () => {
    if (loading) return;

    try {
      setLoading(true);

      if (isWatching) {
        // Unwatch
        const response = await enhancedApiService({
          method: 'DELETE',
          url: `/issues/${issueId}/watchers`
        });

        if (response.success) {
          setIsWatching(false);
          setWatcherCount(prev => Math.max(0, prev - 1));
          showNotification('No longer watching this issue', 'success');

          // Reload to get updated watchers list
          loadWatchStatus();
        }
      } else {
        // Watch
        const response = await enhancedApiService({
          method: 'POST',
          url: `/issues/${issueId}/watchers`
        });

        if (response.success) {
          setIsWatching(true);
          setWatcherCount(prev => prev + 1);
          showNotification('Now watching this issue', 'success');

          // Reload to get updated watchers list
          loadWatchStatus();
        }
      }
    } catch (error) {
      console.error('Error toggling watch status:', error);
      showNotification('Failed to update watch status', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoad) {
    return (
      <button
        disabled
        className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-400 bg-white cursor-not-allowed"
      >
        <EyeIcon className="h-4 w-4" />
        <span>...</span>
      </button>
    );
  }

  return (
    <button
      onClick={handleToggleWatch}
      disabled={loading}
      className={`inline-flex items-center gap-2 px-3 py-2 border rounded-md shadow-sm text-sm font-medium transition-colors ${
        isWatching
          ? 'border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100'
          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
      } disabled:opacity-50 disabled:cursor-not-allowed`}
      title={isWatching ? 'Click to unwatch this issue' : 'Click to watch this issue'}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
      ) : isWatching ? (
        <EyeIconSolid className="h-4 w-4" />
      ) : (
        <EyeIcon className="h-4 w-4" />
      )}
      <span>{isWatching ? 'Watching' : 'Watch'}</span>
      {watcherCount > 0 && (
        <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${
          isWatching ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {watcherCount}
        </span>
      )}
    </button>
  );
};

export default WatchButton;
